import { useState } from 'react';
import { KeyRound, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
    useClearSshCredential,
    useSetSshCredential,
    useSshCredential,
} from '@/hooks/deployment';

/**
 * There is deliberately no read-back endpoint, so this only ever shows the
 * shape of what is stored — never a value.
 */
export function SshCredentialCard({ deploymentId }: { deploymentId: string }) {
    const { data: credential, isLoading } = useSshCredential(deploymentId);
    const setMutation = useSetSshCredential(deploymentId);
    const clearMutation = useClearSshCredential(deploymentId);

    const [open, setOpen] = useState(false);
    const [type, setType] = useState<'PASSWORD' | 'PRIVATE_KEY'>('PRIVATE_KEY');
    const [secret, setSecret] = useState('');

    const save = () => {
        setMutation.mutate(
            { type, secret },
            {
                onSuccess: () => {
                    setOpen(false);
                    setSecret('');
                },
            },
        );
    };

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <KeyRound className="h-4 w-4" />
                    SSH access
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {isLoading ? (
                    <p className="text-sm text-slate-500">Checking…</p>
                ) : credential ? (
                    <div className="space-y-1 text-sm">
                        <p>
                            Using a saved{' '}
                            <span className="font-medium">
                                {credential.type === 'PRIVATE_KEY' ? 'private key' : 'password'}
                            </span>{' '}
                            for this host.
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Encrypted at rest (key v{credential.keyVersion}), last updated{' '}
                            {new Date(credential.updatedAt).toLocaleString()}. The value cannot be
                            read back.
                        </p>
                    </div>
                ) : (
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Using the provisioner's fleet key. Add an override only if this host
                        cannot accept it.
                    </p>
                )}

                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
                        {credential ? 'Replace' : 'Add override'}
                    </Button>
                    {credential && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => clearMutation.mutate()}
                            disabled={clearMutation.isPending}
                        >
                            <Trash2 className="mr-2 h-3.5 w-3.5" />
                            Use the fleet key
                        </Button>
                    )}
                </div>
            </CardContent>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Per-host SSH credential</DialogTitle>
                        <DialogDescription>
                            Stored encrypted with AES-256-GCM and only ever decrypted to hand to
                            the provisioner for one run.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 py-2">
                        <RadioGroup
                            value={type}
                            onValueChange={(v) => setType(v as 'PASSWORD' | 'PRIVATE_KEY')}
                            className="flex gap-4"
                        >
                            <div className="flex items-center gap-2">
                                <RadioGroupItem value="PRIVATE_KEY" id="ssh-card-key" />
                                <Label htmlFor="ssh-card-key" className="font-normal">
                                    Private key
                                </Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <RadioGroupItem value="PASSWORD" id="ssh-card-pw" />
                                <Label htmlFor="ssh-card-pw" className="font-normal">
                                    Password
                                </Label>
                            </div>
                        </RadioGroup>
                        <Textarea
                            value={secret}
                            onChange={(e) => setSecret(e.target.value)}
                            rows={type === 'PRIVATE_KEY' ? 6 : 1}
                            placeholder={
                                type === 'PRIVATE_KEY'
                                    ? '-----BEGIN OPENSSH PRIVATE KEY-----'
                                    : 'SSH password'
                            }
                            className="font-mono text-xs"
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={save}
                            disabled={!secret.trim() || setMutation.isPending}
                        >
                            {setMutation.isPending ? 'Saving…' : 'Save credential'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
